/**
 * Metriek & Meetkunde (MET) — 39 families, 9 skills
 *
 * Skills: lengte (5), gewicht (4), inhoud (6), oppervlakte (8),
 *         cirkels (4), hoeken (4), figuren (3), schaal (3), tijd_meten (2)
 *
 * Split from taxonomyRegistry.ts in R23.
 *
 * LDG Didactisch Audit Ronde 2 (7 apr 2026): 2 tier-aanpassingen:
 * - MET-OPP-DRIEHOEK: E6+ (tier 6) → M7+ (tier 10) — QF-8 reversed order (PARALLELLOGRAM before DRIEHOEK),
 *   but tiers were not updated. DRIEHOEK now depends on PARALLELLOGRAM (tier 9), so must be ≥9.
 * - MET-OPP-SAMENGESTELD: B7 (tier 7) → E7 (tier 11) — depends on DRIEHOEK (now tier 10), must be ≥10.
 */

import type { FamilyDefinition } from '../types'

export const MET_FAMILIES: FamilyDefinition[] = [
  // 2.1 Skill: lengte (5 families)
  {
    familyId: 'MET-LEN-OMREKEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_omrekenen',
    tier: 1,
    levelCode: 'B6',
    description: 'Het metrieke stelsel (trapje): mm ↔ cm ↔ dm ↔ m ↔ km',
  },
  {
    familyId: 'MET-LEN-SCHATTEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_schatten',
    tier: 2,
    levelCode: 'B6+',
    description: 'Lengtes schatten',
  },
  {
    familyId: 'MET-LEN-METEN',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_meten',
    tier: 1,
    levelCode: 'B6',
    description: 'Lengtes meten met liniaal',
  },
  {
    familyId: 'MET-LEN-NAUWKEURIG',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_nauwkeurig',
    tier: 5,
    levelCode: 'E6',
    description: 'Nauwkeurig meten (mm precisie)',
  },
  {
    familyId: 'MET-LEN-CONTEXT',
    module: 'Metriek',
    skill: 'lengte',
    subSkill: 'lengte_context',
    tier: 7,
    levelCode: 'B7',
    description: 'Lengtes in context (afstand, hoogte)',
  },
  // 2.2 Skill: gewicht (4 families)
  {
    familyId: 'MET-GEW-OMREKEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_omrekenen',
    tier: 3,
    levelCode: 'M6',
    description: 'Gewicht omrekenen (g/kg/ton)',
  },
  {
    familyId: 'MET-GEW-SCHATTEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_schatten',
    tier: 3,
    levelCode: 'M6',
    description: 'Gewicht schatten',
  },
  {
    familyId: 'MET-GEW-WEGEN',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_wegen',
    tier: 2,
    levelCode: 'B6+',
    description: 'Wegen met weegschaal',
  },
  {
    familyId: 'MET-GEW-CONTEXT',
    module: 'Metriek',
    skill: 'gewicht',
    subSkill: 'gewicht_context',
    tier: 7,
    levelCode: 'B7',
    description: 'Gewicht in context',
  },
  // 2.3 Skill: inhoud (6 families)
  {
    familyId: 'MET-INH-OMREKEN',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_omrekenen',
    tier: 5,
    levelCode: 'E6',
    description: 'Inhoud omrekenen (ml/cl/dl/l)',
  },
  {
    familyId: 'MET-INH-LITER',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_liter_brug',
    tier: 9,
    levelCode: 'M7',
    description: 'De koppeling dm³ = liter',
  },
  {
    familyId: 'MET-INH-OMREKEN-KUBIEK',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_kubiek_omreken',
    tier: 11,
    levelCode: 'E7',
    description: 'm³ ↔ dm³ ↔ liter verdieping (kerndoel)',
  },
  {
    familyId: 'MET-INH-CITER',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_kleine_maten',
    tier: 12,
    levelCode: 'E7+',
    description: 'Kleine inhoudsmaten: cc/cl/dl (fijnmazig omrekenen)',
  },
  {
    familyId: 'MET-INH-BLOK',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inh_blok',
    tier: 6,
    levelCode: 'E6+',
    description: 'Inhoud balk: L × B × H',
  },
  {
    familyId: 'MET-INH-SCHATTEN',
    module: 'Metriek',
    skill: 'inhoud',
    subSkill: 'inhoud_schatten',
    tier: 7,
    levelCode: 'B7',
    description: 'Inhoud schatten',
  },
  // 2.4 Skill: oppervlakte (8 families)
  {
    familyId: 'MET-OPP-RECHT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_rechthoek',
    tier: 5,
    levelCode: 'E6',
    description: 'Oppervlakte rechthoek: L × B formule',
  },
  {
    familyId: 'MET-OMTREK-RECHT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'omtrek_rechthoek',
    tier: 5,
    levelCode: 'E6',
    description: 'Omtrek rechthoek: 2L + 2B formule',
  },
  {
    familyId: 'MET-OPP-DRIEHOEK',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_driehoek',
    tier: 10,
    levelCode: 'M7+',
    description: 'Oppervlakte driehoek: basis × hoogte ÷ 2',
  },
  {
    familyId: 'MET-OPP-SAMENGESTELD',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_samengesteld',
    tier: 11,
    levelCode: 'E7',
    description: 'Samengestelde figuren: delen en optellen',
  },
  {
    familyId: 'MET-OPP-LAND',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_landmaten',
    tier: 10,
    levelCode: 'M7+',
    description: 'Landmaten: are/hectare conversie',
  },
  {
    familyId: 'MET-OPP-VIERKANT',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_vierkant',
    tier: 5,
    levelCode: 'E6',
    description: 'Oppervlakte vierkant',
  },
  {
    familyId: 'MET-OPP-PARALLELLOGRAM',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_parallellogram',
    tier: 9,
    levelCode: 'M7',
    description: 'Oppervlakte parallellogram',
  },
  {
    familyId: 'MET-OPP-OMREKENEN',
    module: 'Metriek',
    skill: 'oppervlakte',
    subSkill: 'opp_omrekenen',
    tier: 11,
    levelCode: 'E7',
    description: 'Oppervlakte-eenheden omrekenen',
  },
  // 2.5 Skill: cirkels (4 families)
  {
    familyId: 'MET-MEETK-OMTREK',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_omtrek_cirkel',
    tier: 14,
    levelCode: 'M8',
    description: 'Omtrek cirkel: introductie van π',
  },
  {
    familyId: 'MET-MEETK-OPP',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_opp_cirkel',
    tier: 15,
    levelCode: 'E8',
    description: 'Oppervlakte cirkel: kwadratisch rekenen met π',
  },
  {
    familyId: 'MET-MEETK-DIAMETER',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_diameter_straal',
    tier: 13,
    levelCode: 'B8',
    description: 'Diameter en straal',
  },
  {
    familyId: 'MET-MEETK-CIRKEL-DEEL',
    module: 'Metriek',
    skill: 'cirkels',
    subSkill: 'meetk_cirkelsector',
    tier: 16,
    levelCode: 'E8+',
    description: 'Cirkelsector en -segment',
  },
  // 2.6 Skill: hoeken (4 families)
  {
    familyId: 'MET-HOEK-METEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_meten',
    tier: 7,
    levelCode: 'B7',
    description: 'Hoeken meten met geodriehoek',
  },
  {
    familyId: 'MET-HOEK-TEKENEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_tekenen',
    tier: 7,
    levelCode: 'B7',
    description: 'Hoeken tekenen',
  },
  {
    familyId: 'MET-HOEK-SOORTEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_soorten',
    tier: 9,
    levelCode: 'M7',
    description: 'Hoeksoorten (recht/stomp/scherp)',
  },
  {
    familyId: 'MET-HOEK-BEREKENEN',
    module: 'Metriek',
    skill: 'hoeken',
    subSkill: 'hoek_berekenen',
    tier: 13,
    levelCode: 'B8',
    description: 'Hoeken berekenen (som 180°)',
  },
  // 2.7 Skill: figuren (3 families)
  {
    familyId: 'MET-DRIEHOEK-SOORTEN',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'driehoek_soorten',
    tier: 9,
    levelCode: 'M7',
    description: 'Gelijkzijdig/gelijkbenig/rechthoekig (kerndoel)',
  },
  {
    familyId: 'MET-SYMMETRIE',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'symmetrie_spiegeling',
    tier: 7,
    levelCode: 'B7',
    description: 'Spiegeling en symmetrie (kerndoel)',
  },
  {
    familyId: 'MET-COORDINATEN',
    module: 'Metriek',
    skill: 'figuren',
    subSkill: 'coordinaten_assenstelsel',
    tier: 9,
    levelCode: 'M7',
    description: 'Assenstelsel basis (CITO)',
  },
  // 2.8 Skill: schaal (3 families)
  {
    familyId: 'MET-SCHAAL-BASIS',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_basis',
    tier: 9,
    levelCode: 'M7',
    description: 'Schaal basis (1:100)',
  },
  {
    familyId: 'MET-SCHAAL-LENGTE',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_lengte',
    tier: 11,
    levelCode: 'E7',
    description: 'Schaal bij lengte',
  },
  {
    familyId: 'MET-SCHAAL-OPP',
    module: 'Metriek',
    skill: 'schaal',
    subSkill: 'schaal_oppervlakte',
    tier: 16,
    levelCode: 'E8+',
    description: 'Schaal bij oppervlakte: schaal in het kwadraat',
  },
  // 2.9 Skill: tijd_meten (2 families)
  {
    familyId: 'MET-TIJD-METEN',
    module: 'Metriek',
    skill: 'tijd_meten',
    subSkill: 'tijd_meten_stopwatch',
    tier: 3,
    levelCode: 'M6',
    description: 'Tijd meten met stopwatch',
  },
  {
    familyId: 'MET-TIJD-NAUWKEURIG',
    module: 'Metriek',
    skill: 'tijd_meten',
    subSkill: 'tijd_nauwkeurig_meten',
    tier: 7,
    levelCode: 'B7',
    description: 'Nauwkeurig tijdmeten (seconden/tienden)',
  },
]
